import { useRef } from "react";
import { motion, useInView, useReducedMotion, useMotionValue, useMotionValueEvent } from "framer-motion";

// Ink-pour reveal: organic blobs bleed open from the top-center of the panel,
// like ink dropping onto wet paper. Drives a multi-blob CSS mask.
const blobMask = (p) => {
  // p: 0..1 pour progress
  const r = (k) => Math.max(0, Math.min(1, (p - k) / (1 - k))) * 75;
  return [
    `radial-gradient(circle at 50% ${8 + p * 14}%, rgba(0,0,0,1) ${r(0) * 0.62}%, rgba(0,0,0,0.85) ${r(0) * 0.8}%, transparent ${r(0)}%)`,
    `radial-gradient(circle at ${32 - p * 6}% ${30 + p * 26}%, rgba(0,0,0,1) ${r(0.12) * 0.55}%, transparent ${r(0.12)}%)`,
    `radial-gradient(circle at ${68 + p * 5}% ${34 + p * 30}%, rgba(0,0,0,1) ${r(0.2) * 0.55}%, transparent ${r(0.2)}%)`,
    `radial-gradient(circle at ${46 + p * 4}% ${66 + p * 20}%, rgba(0,0,0,1) ${r(0.34) * 0.6}%, transparent ${r(0.34)}%)`,
  ].join(", ");
};

export const InkReveal = ({ children, className = "" }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "140px" });
  const p = useMotionValue(0);

  useMotionValueEvent(p, "change", (v) => {
    if (ref.current) {
      const m = blobMask(v);
      ref.current.style.webkitMaskImage = m;
      ref.current.style.maskImage = m;
    }
  });

  if (reduce) return <div className={className}>{children}</div>;

  return (
    <motion.div
      ref={ref}
      className={className}
      style={{ WebkitMaskImage: blobMask(0), maskImage: blobMask(0) }}
    >
      <motion.div
        initial={false}
        animate={inView ? { opacity: 1 } : {}}
        onUpdate={() => {}}
        style={{ display: "contents" }}
      />
      <PourDriver inView={inView} p={p} />
      {children}
    </motion.div>
  );
};

// Drives the pour progress with a soft spring when the panel scrolls in.
const PourDriver = ({ inView, p }) => {
  const started = useRef(false);
  if (inView && !started.current) {
    started.current = true;
  }
  return (
    <motion.div
      style={{ display: "none" }}
      animate={started.current ? { v: 1 } : {}}
      initial={{ v: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      onUpdate={(latest) => p.set(latest.v)}
    />
  );
};
